import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { Activity as ActivityIcon } from 'lucide-react'
import { api } from '../lib/api'
import { actionVerb, formatDateTime } from '../lib/format'
import { Card, CardBody, SectionHeading } from '../components/ui/Card'
import { DataTable, Pagination } from '../components/ui/DataTable'
import { SearchField, SelectField } from '../components/ui/Field'
import { useToast } from '../components/ui/Toast'

const ACTIONS = [
  { value: '', label: 'Every action' },
  { value: 'tenant.provisioned', label: 'Tenant provisioned' },
  { value: 'tenant.suspended', label: 'Tenant suspended' },
  { value: 'tenant.reactivated', label: 'Tenant reactivated' },
  { value: 'tenant.migrated', label: 'Tenant migrated' },
  { value: 'tenant.backed_up', label: 'Tenant backed up' },
  { value: 'tenant.deleted', label: 'Tenant deleted' },
  { value: 'admin.added', label: 'Administrator added' },
  { value: 'admin.revoked', label: 'Administrator revoked' },
]

/**
 * Every recorded platform-admin action — provisioning, suspending,
 * migrating, backing up or deleting a tenant; adding or revoking another
 * administrator. The dashboard's own "Recent activity" card is only a
 * five-item preview of this; this page is the actual audit trail, with
 * search and filtering, for anyone establishing who did what and when.
 */
export default function Activity() {
  const toast = useToast()

  const [rows, setRows] = useState(null)
  const [meta, setMeta] = useState(null)
  const [search, setSearch] = useState('')
  const [action, setAction] = useState('')
  const [page, setPage] = useState(1)
  const [perPage, setPerPage] = useState(25)

  async function load() {
    try {
      const response = await api.raw.get('/platform/activity', {
        params: {
          search: search || undefined,
          action: action || undefined,
          page,
          per_page: perPage,
        },
      })
      setRows(response.data ?? [])
      setMeta(response.meta ?? null)
    } catch (err) {
      toast.error(err.message)
      setRows([])
    }
  }

  useEffect(() => {
    const timer = setTimeout(load, search ? 350 : 0)
    return () => clearTimeout(timer)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search, action, page, perPage])

  // A new search or filter starts back at page 1 — staying on, say, page 4
  // of an unrelated result set would silently show "no results" instead of
  // the match that is actually there.
  useEffect(() => { setPage(1) }, [search, action])

  const columns = [
    {
      key: 'summary',
      header: 'Activity',
      render: (row) => (
        <div>
          <p className="text-[var(--color-ink-900)]">
            <span className="font-medium">{row.actor_name ?? 'Someone'}</span>{' '}
            {actionVerb(row.action)}{' '}
            {row.tenant_name && (
              row.tenant_id ? (
                <Link to={`/tenants/${row.tenant_id}`} className="font-medium text-[var(--color-accent-text)] hover:underline">
                  {row.tenant_name}
                </Link>
              ) : (
                <span className="font-medium">{row.tenant_name}</span>
              )
            )}
          </p>
          {row.summary && (
            <p className="mt-0.5 text-xs text-[var(--color-ink-500)]">{row.summary}</p>
          )}
        </div>
      ),
    },
    {
      key: 'created_at',
      header: 'When',
      width: '200px',
      nowrap: true,
      render: (row) => (
        <span className="text-[var(--color-ink-500)]">{formatDateTime(row.created_at)}</span>
      ),
    },
  ]

  return (
    <div className="space-y-4">
      <SectionHeading
        level={1}
        title="Activity"
        description="A record of every action taken from this panel — who provisioned, suspended, migrated, backed up or deleted a tenant, and when."
      />

      <Card>
        <div className="flex flex-wrap items-center gap-3 border-b border-[var(--color-line)] p-3">
          <SearchField
            wrapperClassName="max-w-xs"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            placeholder="Actor, tenant or summary"
            aria-label="Search activity"
          />
          <SelectField
            className="w-auto"
            value={action}
            onChange={(event) => setAction(event.target.value)}
            options={ACTIONS}
            aria-label="Filter by action"
          />
        </div>

        <CardBody padded={false} className="flex min-h-[24rem] flex-col">
          <DataTable
            columns={columns}
            rows={rows ?? []}
            loading={rows === null}
            empty={
              <div className="flex flex-col items-center justify-center px-6 py-14 text-center">
                <ActivityIcon size={20} className="mb-3 text-[var(--color-ink-400)]" aria-hidden="true" />
                <h3 className="text-sm font-semibold text-[var(--color-ink-900)]">
                  {search || action ? 'Nothing matches that' : 'No activity yet'}
                </h3>
                <p className="mt-1 max-w-sm text-sm text-[var(--color-ink-500)]">
                  {search || action
                    ? 'Try a different search or filter.'
                    : 'Provisioning, suspending or migrating a tenant will show up here.'}
                </p>
              </div>
            }
          />

          <Pagination
            meta={meta}
            onPageChange={setPage}
            perPage={perPage}
            onPerPageChange={setPerPage}
          />
        </CardBody>
      </Card>
    </div>
  )
}
